import React, { createContext, useState, useEffect, useContext, useRef, useCallback } from 'react';
import Swal from 'sweetalert2';
import axios from 'axios';
import { connectWebSocket, reconnectAuthenticatedWebSocket } from '../config/WebsocketService';
import { queryClient } from '../config/queryClient';
import { invalidateStockSetupStatus } from '../hooks/useStockSetup';
import { authService } from '../services/AuthService';
import {
    updateDeliveryCommonConfig,
    updateDeliveryCustomConfig,
    updateWhatsappConfig,
} from '../services/configService';
import { getApiErrorMessage } from '../utils/apiErrorUtils';
import { getShop } from '../services/ShopService';

const ConfigContext = createContext();

const WHATSAPP_SAVE_DEBOUNCE_MS = 400;

const mergeWhatsappConfig = (currentConfig, savedConfig) => ({
    ...currentConfig,
    ...savedConfig,
    priceActive: savedConfig.priceActive ?? savedConfig.isPriceActive ?? currentConfig.priceActive,
    messageWppActive: savedConfig.messageWppActive ?? savedConfig.isMessageWppActive ?? currentConfig.messageWppActive,
    ruralSettings: savedConfig.ruralSettings ?? savedConfig.isRuralSettings ?? currentConfig.ruralSettings,
    deliveryCommon: savedConfig.deliveryCommon ?? currentConfig.deliveryCommon,
    deliveryCustom: savedConfig.deliveryCustom ?? currentConfig.deliveryCustom,
});

const ConfigProvider = ({ children }) => {
  const [config, setConfig] = useState({
                                                      "id": 1,
                                                      "orderStartTime": null,
                                                      "orderEndTime": null,
                                                      "deliveryType": "COMMON",
                                                      "idDelivery": 1,
                                                      "isPriceActive": 1,
                                                      "isMessageWppActive": 1,
                                                      "shopBranchId": 1,
                                                      "status": false,
                                                      "nameFrontFilter": "isOrderActive",
                                                      "verifyAddress": false,
                                                      "ruralSettings":false,
                                                      "priceRuralBase":"00",
                                                      "deliveryCommon": {
                                                          "id": 0,
                                                          "priceBase": "00",
                                                          "metersBase": 0,
                                                          "priceMetersExcessive": "00",
                                                          "cantMetersExcessive": 0,
                                                          "metersMax": 0,
                                                          "nameFrontFilter": "isDelivery",
                                                          "active": false
                                                      },
                                                      "deliveryCustom":{
                                                            "id": 0,
                                                            "priceBase": "00",
                                                            "rangeMaxMeters": 0,
                                                            "nameFrontFilter": "isDelivery",
                                                            "active": false
                                                      },
                                                      "priceActive": true
  });

  const [features, setFeatures] = useState([
                                                                      {
                                                                          "id": 1,
                                                                          "name": "MODULO BASICO",
                                                                          "status": true,
                                                                          "createdAt": null
                                                                      },
                                                                      {
                                                                          "id": 2,
                                                                          "name": "REPORTES",
                                                                          "status": false,
                                                                          "createdAt": null
                                                                      },
                                                                      {
                                                                          "id": 3,
                                                                          "name": "CONFIGURACION EXTENDIDA",
                                                                          "status": false,
                                                                          "createdAt": null
                                                                      },
                                                                      {
                                                                          "id": 4,
                                                                          "name": "GEOLOCALIZACION",
                                                                          "status": false,
                                                                          "createdAt": null
                                                                      }
                                                                  ]);

  const [shopPlanInfo, setShopPlanInfo] = useState(null);

    const configRef = useRef(config);
    const whatsappSaveDebounceRef = useRef(null);
    const pendingWhatsappSaveRef = useRef(null);

    useEffect(() => {
        configRef.current = config;
    }, [config]);

    const invalidateBranchConfigCache = () => {
        queryClient.invalidateQueries({ queryKey: ['branchConfig'] });
    };

    const resyncWhatsappConfigFromServer = async () => {
        try {
            const response = await axios.get(process.env.REACT_APP_API_MODULES_CONFIGS_ROUTE, {
                withCredentials: true,
            });
            if (response.data) {
                configRef.current = response.data;
                setConfig(response.data);
            }
        } catch (syncError) {
            console.error('No se pudo resincronizar la configuración', syncError);
        }
    };

    const persistWhatsappConfig = useCallback((updatedConfig, successMessage) => {
        configRef.current = updatedConfig;
        setConfig(updatedConfig);

        return new Promise((resolve, reject) => {
            if (whatsappSaveDebounceRef.current) {
                clearTimeout(whatsappSaveDebounceRef.current);
            }

            pendingWhatsappSaveRef.current = { resolve, reject, successMessage };

            whatsappSaveDebounceRef.current = setTimeout(async () => {
                const pending = pendingWhatsappSaveRef.current;
                pendingWhatsappSaveRef.current = null;
                whatsappSaveDebounceRef.current = null;

                if (!pending) {
                    return;
                }

                try {
                    const savedConfig = await updateWhatsappConfig(configRef.current);
                    const merged = mergeWhatsappConfig(configRef.current, savedConfig);
                    configRef.current = merged;
                    setConfig(merged);
                    invalidateBranchConfigCache();
                    if (pending.successMessage) {
                        showSuccessMessage(pending.successMessage);
                    }
                    pending.resolve(merged);
                } catch (error) {
                    await resyncWhatsappConfigFromServer();
                    showErrorMessage(getApiErrorMessage(error, 'No se pudo guardar la configuración.'));
                    pending.reject(error);
                }
            }, WHATSAPP_SAVE_DEBOUNCE_MS);
        });
    }, []);

    const persistDeliveryCommonConfig = async (updatedConfig, successMessage) => {
        const previousConfig = config;

        setConfig(updatedConfig);

        try {
            const savedDelivery = await updateDeliveryCommonConfig(updatedConfig.deliveryCommon);
            const nextConfig = {
                ...updatedConfig,
                deliveryCommon: savedDelivery,
            };
            setConfig(nextConfig);
            invalidateBranchConfigCache();
            showSuccessMessage(successMessage);
        } catch (error) {
            setConfig(previousConfig);
            showErrorMessage(getApiErrorMessage(error, 'No se pudo guardar la configuración de delivery.'));
            throw error;
        }
    };

    const persistDeliveryCustomConfig = async (updatedConfig, successMessage) => {
        const previousConfig = config;

        setConfig(updatedConfig);

        try {
            const savedDelivery = await updateDeliveryCustomConfig(updatedConfig.deliveryCustom);
            const nextConfig = {
                ...updatedConfig,
                deliveryCustom: savedDelivery,
            };
            setConfig(nextConfig);
            invalidateBranchConfigCache();
            showSuccessMessage(successMessage);
        } catch (error) {
            setConfig(previousConfig);
            showErrorMessage(getApiErrorMessage(error, 'No se pudo guardar la configuración de delivery.'));
            throw error;
        }
    };


    /******************************************************
            METODOS GENERICOS DE CONFIGURACIONES
     ******************************************************/

    const mapShopPlanInfo = (data) => ({
        shopPlan: data.shopPlan,
        shopPlanDisplayName: data.shopPlanDisplayName,
        currentShopBranchCount: data.currentShopBranchCount,
        maxShopBranches: data.maxShopBranches,
    });

    const fetchShopPlan = async () => {
        try {
            const data = await getShop();
            setShopPlanInfo(mapShopPlanInfo(data));
            return data;
        } catch (error) {
            console.error('Error al obtener plan del local', error);
            return null;
        }
    };

    const reloadShopPlan = fetchShopPlan;

    // Método para obtener las flag de modulos NEGOCIO (visualiza reportes - stock , etc )
    const fetchFeatures = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_FEATURE_SELECTOR_ROUTE}`, {
                withCredentials: true
            });
            setFeatures(response.data);
            
            window.dispatchEvent(new Event('featuresLoaded'));
        } catch (error) {
            console.error('Error al obtener las características', error);
            window.dispatchEvent(new Event('featuresLoaded'));
        }
    };

    //Metodo para obtener toda la config de whatsap y delivery
    const fetchConfigs = async () => {
    try {
      const response = await axios.get(process.env.REACT_APP_API_MODULES_CONFIGS_ROUTE, {
          withCredentials: true
      });
      if (response.data) {
           setConfig(response.data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
      let isMounted = true;

      const connectRealtimeConfig = () => {
          connectWebSocket((newConfig) => {
              if (typeof newConfig === 'function') {
                  setConfig((prevConfig) => newConfig(prevConfig));
              } else {
                  setConfig((prevConfig) => mergeWhatsappConfig(prevConfig, newConfig));
              }
              invalidateBranchConfigCache();
          });
      };

      const loadAuthenticatedConfig = async () => {
          try {
              const isAuth = await authService.isAuthenticated();
              if (!isMounted || !isAuth) {
                  return;
              }

              connectRealtimeConfig();
              await fetchConfigs();
              await fetchFeatures();
              await fetchShopPlan();
          } catch (error) {
              console.warn('No se pudo verificar sesión para cargar configuraciones:', error);
          }
      };

      loadAuthenticatedConfig();

      const handleLoginSuccess = async () => {
          connectRealtimeConfig();
          reconnectAuthenticatedWebSocket();
          invalidateStockSetupStatus(queryClient);
          await fetchConfigs();
          await fetchFeatures();
          await fetchShopPlan();
      };

      window.addEventListener('loginSuccess', handleLoginSuccess);

      return () => {
          isMounted = false;
          window.removeEventListener('loginSuccess', handleLoginSuccess);
      };
  }, []);

  // Función para recargar la configuración manualmente
  const reloadConfigs = async () => {
    try {
      await fetchConfigs();
      await fetchFeatures();
    } catch (error) {
      console.error('Error al recargar configuraciones:', error);
    }
  };



    /******************************************************
            METODOS DE CONFIGURACIONES DE WHATSAPP
     ******************************************************/

   // Método que se llama cuando se cambia la configuracion de pedido activo
    const toggleOrders = async (isChecked) => {
        const currentConfig = configRef.current;
        const updatedConfig = {
            ...currentConfig,
            status: isChecked,
        };

        if (isChecked && currentConfig.priceActive === false) {
            showErrorMessage('Se tienen que activar la visualizacion de precios.');
            return;
        }

        await persistWhatsappConfig(updatedConfig, 'Estado pedidos actualizado.');
    };

    // Método que se llama cuando se cambia la configuracion de visualizacion de precios
    const toggleActivePrice = async (newActivePrice) => {
        const currentConfig = configRef.current;
        const updatedConfig = {
            ...currentConfig,
            priceActive: newActivePrice,
        };

        if (newActivePrice === false && currentConfig.status === true) {
            showErrorMessage('Se tiene que desactivar los pedidos.');
            return;
        }

        await persistWhatsappConfig(updatedConfig, 'Estado visualizacion precios actualizado.');
    };

    // Método que se llama cuando se cambia la configuracion de notificacion por wpp
    const toggleActiveMessageWpp = async (newActiveMessageWpp) => {
        const updatedConfig = {
            ...configRef.current,
            messageWppActive: newActiveMessageWpp,
        };
        await persistWhatsappConfig(updatedConfig, 'Estado notificacion por WhatsApp actualizado.');
    };

    // Método que se llama cuando se cambia la configuracion de verificacion de ubicacion
    const toggleVerifyAddress = async (isChecked) => {
        const updatedConfig = {
            ...configRef.current,
            verifyAddress: isChecked,
        };
        await persistWhatsappConfig(updatedConfig, 'Estado verificacion ubicaciones actualizado.');
    };

    // Método que se llama cuando se cambia la configuracion de verificacion zona rural
    const toggleVerifyZonaRural = async (isChecked) => {
        const updatedConfig = {
            ...configRef.current,
            ruralSettings: isChecked,
        };
        await persistWhatsappConfig(updatedConfig, 'Estado zona rural actualizado.');
    };

    // Método que se llama cuando se cambia la configuracion de precio de delivery
    const changeDeliveryPriceRural = async (newDeliveryPrice) => {
        const updatedConfig = {
            ...configRef.current,
            priceRuralBase: newDeliveryPrice,
        };
        await persistWhatsappConfig(updatedConfig, 'Precio delivery rural actualizado.');
    };


    function isDeliveryActive(config) {
        if (config.idDelivery !== null &&  config.deliveryType !== "NO_DELIVERIES") {
            return config.deliveryType === 'COMMON'
                ? config.deliveryCommon.active
                : config.deliveryType === 'CUSTOM'
                    ? config.deliveryCustom.active
                    : false;
        }
        return false;
    }

    /******************************************************
            METODOS DE CONFIGURACIONES DE DELIVERY COMMON
     ******************************************************/

    // Método que se llama cuando se cambia la configuracion de status de delivery
    const toggleDelivery = async (isChecked) => {
        const updatedConfig = {
            ...config,
            deliveryCommon: {
                ...config.deliveryCommon,
                active: isChecked,
            },
        };
        await persistDeliveryCommonConfig(updatedConfig, 'Estado delivery actualizado.');
    };

    // Método que se llama cuando se cambia la configuracion de precio de delivery
    const changeDeliveryPrice = async (newDeliveryPrice) => {
        const updatedConfig = {
            ...config,
            deliveryCommon: {
                ...config.deliveryCommon,
                priceBase: newDeliveryPrice,
            },
        };
        await persistDeliveryCommonConfig(updatedConfig, 'Precio delivery actualizado.');
    };

    // Método que se llama cuando se cambia la configuracion de distancia de delivery
    const changeDeliveryRange = async (newDeliveryRange) => {
        const updatedConfig = {
            ...config,
            deliveryCommon: {
                ...config.deliveryCommon,
                rangeMaxMeters: newDeliveryRange,
            },
        };
        await persistDeliveryCommonConfig(updatedConfig, 'Rango delivery actualizado.');
    };



    /******************************************************
        METODOS DE CONFIGURACIONES DE DELIVERY CUSTOM
     ******************************************************/

        // Método que se llama cuando se cambia la configuracion de status de delivery
    const toggleDeliveryCustom = async (isChecked) => {
            const updatedConfig = {
                ...config,
                deliveryCustom: {
                    ...config.deliveryCustom,
                    active: isChecked,
                },
            };
            await persistDeliveryCustomConfig(updatedConfig, 'Estado delivery actualizado.');
        };

    // Método que se llama cuando se cambia la configuracion de precio de delivery
    const changeDeliveryPriceCustom = async (newDeliveryPrice) => {
        const updatedConfig = {
            ...config,
            deliveryCustom: {
                ...config.deliveryCustom,
                priceBase: newDeliveryPrice,
            },
        };
        await persistDeliveryCustomConfig(updatedConfig, 'Precio delivery actualizado.');
    };

    // Método que se llama cuando se cambia la configuracion de distancia de delivery
    const changeDeliveryRangeCustom = async (newDeliveryRange) => {
        const updatedConfig = {
            ...config,
            deliveryCustom: {
                ...config.deliveryCustom,
                metersBase: newDeliveryRange,
            },
        };
        await persistDeliveryCustomConfig(updatedConfig, 'Rango delivery actualizado.');
    };

    // Método que se llama cuando se cambia la configuracion de precio de rangos excedidos
    const changeDeliveryPriceMetersExcessiveCustom = async (newDeliveryPrice) => {
        const updatedConfig = {
            ...config,
            deliveryCustom: {
                ...config.deliveryCustom,
                priceMetersExcessive: newDeliveryPrice,
            },
        };
        await persistDeliveryCustomConfig(updatedConfig, 'Precio delivery actualizado.');
    };

    // Método que se llama cuando se cambia la configuracion de metros de rango de delivery
    const changeDeliveryCantMetersExcessiveCustom = async (newDeliveryRange) => {
        const updatedConfig = {
            ...config,
            deliveryCustom: {
                ...config.deliveryCustom,
                cantMetersExcessive: newDeliveryRange,
            },
        };
        await persistDeliveryCustomConfig(updatedConfig, 'Rango delivery actualizado.');
    };

    // Método que se llama cuando se cambia la configuracion de metros de rango de delivery
    const changeDeliveryMetersMaxCustom = async (newDeliveryRange) => {
        const updatedConfig = {
            ...config,
            deliveryCustom: {
                ...config.deliveryCustom,
                metersMax: newDeliveryRange,
            },
        };
        await persistDeliveryCustomConfig(updatedConfig, 'Rango delivery actualizado.');
    };


    const showSuccessMessage = (message) => {
        Swal.fire({
            icon: 'success',
            title: '¡Éxito!',
            text: message,
            timer: 2000,
            showConfirmButton: false,
        });
    };

    const showErrorMessage = (message) => {
        Swal.fire({
            icon: 'error',
            title: '¡Atención!',
            text: message,
            timer: 2000,
            showConfirmButton: false,
        });
    };

    return (
    <ConfigContext.Provider value={{
        config,
        toggleOrders,
        toggleDelivery,
        toggleVerifyAddress,
        changeDeliveryPrice,
        changeDeliveryRange,
        features,
        toggleActivePrice,
        toggleActiveMessageWpp,
        toggleDeliveryCustom,
        changeDeliveryPriceCustom,
        changeDeliveryRangeCustom,
        changeDeliveryPriceMetersExcessiveCustom,
        changeDeliveryCantMetersExcessiveCustom,
        changeDeliveryMetersMaxCustom,
        toggleVerifyZonaRural,
        changeDeliveryPriceRural,
        reloadConfigs,
        reloadFeatures: fetchFeatures,
        shopPlanInfo,
        reloadShopPlan,
    }}>
      {children}
    </ConfigContext.Provider>
  );
};

export { ConfigProvider, ConfigContext };

export function useConfig() {
  return useContext(ConfigContext);
}
